import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/ProtectedRoute';
import RootRedirect from './components/routing/RootRedirect';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { BrandAdminRoute, PortalRoute, FinanceRoute, ExportsRoute, ShipmentRoute } from './components/BrandAdminRoute';
import { catalogRoutes } from './pages/catalog/catalogRoutes';
import { ADMIN_CATALOG_BASE, CATALOG_BASE } from './pages/catalog/catalogPaths';
import { V2PreviewRoute } from './components/V2PreviewRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { usePermissions } from './hooks/usePermissions';
import { isCatalogEnabled, isStudioEnabled } from './lib/featureFlags';
// v2 page chunks: the same loaders v2 Home preloads on idle (useV2HomePrefetch).
import {
  loadFinancesPage,
  loadHomePage,
  loadOrdersPage,
  loadProductsPage,
  loadReportsLayout,
  loadReturnsPage,
  loadSalesPage,
  loadSettingsPage,
} from './lib/v2RouteChunks';

// Auth pages (kept in main bundle - needed immediately)
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';

// ============================================================================
// Lazy-loaded route components (code splitting)
// ============================================================================

// Report view pages (sub-pages under /reports/* routes)
const StudioPage = React.lazy(() => import('./pages/admin/studio/StudioPage'));
const InventoryReport = React.lazy(() => import('./pages/reports/views/inventory/InventoryReport'));
const InventoryViewsLayout = React.lazy(() => import('./pages/reports/views/inventory/InventoryViewsLayout'));
const RestockRecommendations = React.lazy(() => import('./pages/reports/views/inventory/RestockRecommendations'));
const InventorySnapshot = React.lazy(() => import('./pages/reports/views/inventory/InventorySnapshot'));
const ReturnsTransactions = React.lazy(() => import('./pages/reports/views/returns/ReturnsTransactions'));
const SalesTransactions = React.lazy(() => import('./pages/reports/views/sales/SalesTransactions'));
const SalesUnits = React.lazy(() => import('./pages/reports/views/sales/SalesUnits'));
const PLStatement = React.lazy(() => import('./pages/reports/views/sales/PLStatement'));
const ProductPerformance = React.lazy(() => import('./pages/reports/views/sales/ProductPerformance'));

const Notifications = React.lazy(() => import('./pages/Notifications'));

// Reconcile
const ReconcileLayout = React.lazy(() => import('./pages/reconcile/ReconcileLayout'));
const ReconcileSelfInvoice = React.lazy(() => import('./pages/reconcile/ReconcileSelfInvoice'));
const ReconcileWeekly = React.lazy(() => import('./pages/reconcile/ReconcileWeekly'));

// Reports
const ReportsLayout = React.lazy(loadReportsLayout);
const SalesReports = React.lazy(() => import('./pages/reports/SalesReports'));
const ReturnsReports = React.lazy(() => import('./pages/reports/ReturnsReports'));
const BriefsPage = React.lazy(() => import('./pages/reports/BriefsPage'));
const SavedReportPage = React.lazy(() => import('./pages/reports/SavedReportPage'));
const ListingStatusPage = React.lazy(() => import('./pages/reports/ListingStatusPage'));
const DiscountsPage = React.lazy(() => import('./pages/reports/DiscountsPage'));
const ShipmentsSentPage = React.lazy(() => import('./pages/reports/ShipmentsSentPage'));
// Shipment notices for brands (role-gated, not preview-gated): the composer and the sent list.
const ShipmentsPage = React.lazy(() => import('./pages/products/ShipmentsPage'));
const NewShipmentPage = React.lazy(() => import('./pages/products/NewShipmentPage'));

// Profile
const ProfileLayout = React.lazy(() => import('./pages/profile/ProfileLayout'));
const ProfileGeneral = React.lazy(() => import('./pages/profile/ProfileGeneral'));
const ProfileContact = React.lazy(() => import('./pages/profile/ProfileContact'));
const ProfileDocuments = React.lazy(() => import('./pages/profile/ProfileDocuments'));
const ProfileLogo = React.lazy(() => import('./pages/profile/ProfileLogo'));
const ProfileMarketplaces = React.lazy(() => import('./pages/profile/ProfileMarketplaces'));

// Onboarding
const OnboardingPage = React.lazy(() => import('./pages/onboarding/OnboardingPage'));
const FakeSignPage = React.lazy(() => import('./pages/onboarding/FakeSignPage'));

// V2 Home briefing — preview-flag-gated (see V2PreviewRoute)
const Home = React.lazy(loadHomePage);
// V2 Monthly Statement — preview-flag-gated (see V2PreviewRoute)
const FinancesPage = React.lazy(loadFinancesPage);
// V2 Sales page — preview-flag-gated (see V2PreviewRoute)
const SalesPage = React.lazy(loadSalesPage);
// V2 Products page ("The Stockroom") — preview-flag-gated (see V2PreviewRoute)
const ProductsPage = React.lazy(loadProductsPage);
// V2 Returns page — preview-flag-gated (see V2PreviewRoute)
const ReturnsPage = React.lazy(loadReturnsPage);
// V2 Orders lifecycle page — preview-flag-gated (see V2PreviewRoute)
const OrdersPage = React.lazy(loadOrdersPage);
// V2 Settings (Profile, Team, Notifications, Data exports tabs) — preview-flag-gated.
const SettingsPage = React.lazy(loadSettingsPage);
// Dev-only fixture page for the shared-shell primitives (e2e/shell.spec.ts).
// Dev-only fixture pages: declared only in DEV so a production build tree-shakes them out.
const ShellPreview = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/ShellPreview')) : null;
const HubPreview = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/HubPreview')) : null;
const DsGallery = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/DsGallery')) : null;
const OnboardingPreview = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/OnboardingPreview')) : null;
const SalesPreview = import.meta.env.DEV ? React.lazy(() => import('./pages/dev/SalesPreview')) : null;
// The real frame (sidebar + top bar) around a DEV preview, with a fixture user. Wrap /dev/<page> routes in it.
const DevFrame = import.meta.env.DEV ? React.lazy(() => import('./components/layout/DevFrame')) : null;

// Intelligence Suite (Portal)
const PortalLayout = React.lazy(() => import('./pages/portal/PortalLayout'));
const PortalOverview = React.lazy(() => import('./pages/portal/PortalOverview'));
const PortalTotalView = React.lazy(() => import('./pages/portal/PortalTotalView'));
const PortalCategoryOverview = React.lazy(() => import('./pages/portal/PortalCategoryOverview'));
const StyleDetailView = React.lazy(() => import('./pages/portal/detail/StyleDetailView'));

// Team Management (Brand Admin)
const TeamPage = React.lazy(() => import('./pages/team/TeamPage'));

// Data Exports (Brand Admin + Admin/SUPER_ADMIN)
const DataExportsPage = React.lazy(() => import('./pages/company/exports/DataExportsPage'));

// Brand Hub
const HubPageView = React.lazy(() => import('./pages/hub/HubPageView'));
const MyReportsPage = React.lazy(() => import('./pages/hub/MyReportsPage')); // saved advisor reports

// Admin
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBrandsList = React.lazy(() => import('./pages/admin/AdminBrandsList'));
const AdminBrandDetail = React.lazy(() => import('./pages/admin/AdminBrandDetail'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/users/AdminUsersPage'));
const AdminUserDetail = React.lazy(() => import('./pages/admin/users/AdminUserDetail'));
const AdminPromotionsPage = React.lazy(() => import('./pages/admin/promotions/AdminPromotionsPage'));
const AdminActionRequestsPage = React.lazy(() => import('./pages/admin/AdminActionRequestsPage'));
const AdminOnboardingPage = React.lazy(() => import('./pages/admin/onboarding/AdminOnboardingPage'));
const OnboardingSubmissionDetail = React.lazy(() => import('./pages/admin/onboarding/OnboardingSubmissionDetail'));
const AdminOnboardingRecord = React.lazy(() => import('./pages/admin/onboarding/AdminOnboardingRecord'));
const AdminPortalLayout = React.lazy(() => import('./pages/admin/portal/AdminPortalLayout'));
const AdminReportsLayout = React.lazy(() => import('./pages/admin/reports/AdminReportsLayout'));
const AdminReconcileLayout = React.lazy(() => import('./pages/admin/reconcile/AdminReconcileLayout'));
const AdminSystemHealth = React.lazy(() => import('./pages/admin/system').then(m => ({ default: m.AdminSystemHealth })));
const AdminUserActivityPage = React.lazy(() => import('./pages/admin/activity/AdminUserActivityPage'));
const AdminOperations = React.lazy(() => import('./pages/admin/AdminOperations'));
const ImportHealthBrandDetail = React.lazy(() => import('./pages/admin/ImportHealthBrandDetail'));
const RejectionFeed = React.lazy(() => import('./pages/admin/RejectionFeed'));
const AdminRejectionVerifier = React.lazy(() => import('./pages/admin/rejections/AdminRejectionVerifier'));

// ============================================================================
// Route loading fallback
// ============================================================================

const RouteLoadingFallback = () => (
  <LoadingSpinner size="lg" text="Loading..." fullScreen />
);

// ============================================================================
// Super Admin route guard
// ============================================================================

const SuperAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isSuperAdmin } = usePermissions();

  if (!isSuperAdmin()) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

// ============================================================================
// App
// ============================================================================

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            {/* Public auth routes */}
            <Route path="/sign-in/*" element={<SignIn />} />
            <Route path="/sign-up/*" element={<SignUp />} />
            {/* Dev builds only: fixture page for the E2E overflow sweep. Public
                because Playwright has no Clerk session (Docs/AUDIT_2026-06-10.md T3). */}
            {ShellPreview && DevFrame && <Route path="/dev/shell" element={<DevFrame bare><ShellPreview /></DevFrame>} />}
            {HubPreview && DevFrame && <Route path="/dev/hub" element={<DevFrame><HubPreview /></DevFrame>} />}
            {/* The empty v2 frame: sidebar, top bar and page ground, nothing else.
                /dev/frame/sales acts as /sales (active entry, breadcrumb). */}
            {DevFrame && <Route path="/dev/frame/*" element={<DevFrame bare />} />}
            {SalesPreview && DevFrame && <Route path="/dev/sales" element={<DevFrame><SalesPreview /></DevFrame>} />}
            {/* The REAL v2 pages in the real frame with a fixture user. They read
                tRPC as usual; the visual suite (e2e/visual) answers every read
                from a synthetic fixture. Never point these at real brand data. */}
            {DevFrame && <Route path="/dev/returns" element={<DevFrame><ReturnsPage /></DevFrame>} />}
            {DevFrame && <Route path="/dev/products" element={<DevFrame><ProductsPage /></DevFrame>} />}
            {DevFrame && <Route path="/dev/orders" element={<DevFrame><OrdersPage /></DevFrame>} />}
            {DevFrame && <Route path="/dev/settings" element={<DevFrame><SettingsPage /></DevFrame>} />}
            {OnboardingPreview && <Route path="/dev/onboarding" element={<OnboardingPreview />} />}
            {DevFrame && <Route path="/dev/finances" element={<DevFrame><FinancesPage /></DevFrame>} />}
            {DevFrame && <Route path="/dev/notifications" element={<DevFrame><Notifications /></DevFrame>} />}
            {DevFrame && <Route path="/dev/home" element={<DevFrame><Home /></DevFrame>} />}
            {DevFrame && (
              <Route path="/dev/reports" element={<DevFrame><ReportsLayout /></DevFrame>}>
                <Route path="briefs" element={<BriefsPage />} />
                <Route path="listings" element={<ListingStatusPage />} />
                <Route path="discounts" element={<DiscountsPage />} />
                <Route path="shipments" element={<ShipmentsSentPage />} />
                <Route path="mine/:id" element={<SavedReportPage />} />
              </Route>
            )}
            {/* The design-system gallery: every primitive at every state. */}
            {DsGallery && DevFrame && <Route path="/dev/ds" element={<DevFrame bare><DsGallery /></DevFrame>} />}

            {/* Protected dashboard routes */}
            <Route element={<ProtectedRoute />}>
              {/* Redirect root based on user role: admins go to admin dashboard, others to portal */}
              <Route path="/" element={<RootRedirect />} />

              {/* V2 Home briefing — flag off bounces to /portal/total (Amendment 2026-07-09) */}
              <Route path="/home" element={<V2PreviewRoute><Home /></V2PreviewRoute>} />

              {/* V2 Monthly Statement — same flag gate as Home */}
              <Route path="/finances" element={<V2PreviewRoute><FinancesPage /></V2PreviewRoute>} />

              {/* V2 Sales page — same flag gate as Home. No sidebar/nav
                  changes; reachable by URL only, invisible in prod. */}
              <Route path="/sales" element={<V2PreviewRoute><SalesPage /></V2PreviewRoute>} />

              {/* V2 Products page ("The Stockroom") — same flag gate. No
                  sidebar/nav entry yet (that lands after PR #134); reachable
                  by URL only, invisible in prod. */}
              <Route path="/products" element={<V2PreviewRoute><ProductsPage /></V2PreviewRoute>} />

              {/* V2 Returns page — same flag gate, with a
                  sidebar entry for preview users. */}
              <Route path="/returns" element={<V2PreviewRoute><ReturnsPage /></V2PreviewRoute>} />

              {/* V2 Orders lifecycle report — same flag gate; final home (/sales or
                  /products tab) decided after the design proves out. URL-only. */}
              <Route path="/orders" element={<V2PreviewRoute><OrdersPage /></V2PreviewRoute>} />
              <Route path="/settings" element={<V2PreviewRoute><SettingsPage /></V2PreviewRoute>} />

              {/* Shipment notices (ASN) for the roles that may file one (ShipmentRoute,
                  = the API's ACTING_ROLES). NOT behind V2PreviewRoute: brands file
                  their own notices in production without the v2 /products page. */}
              <Route path="/shipments" element={<ShipmentRoute><ShipmentsPage /></ShipmentRoute>} />
              <Route path="/shipments/new" element={<ShipmentRoute><NewShipmentPage /></ShipmentRoute>} />

              {/* No promotions page (Ari, 2026-10-08): promotions live on
                  Products, in each product's Price tab. Old links land there. */}
              <Route path="/promotion/*" element={<Navigate to="/products" replace />} />

              {/* Intelligence Suite routes — BRAND_ADMIN + BRAND_MANAGER only */}
              <Route path="/portal" element={<PortalRoute><PortalLayout /></PortalRoute>}>
                <Route index element={<PortalOverview />} />
                <Route path="total" element={<PortalTotalView />} />
                <Route path="categories" element={<PortalCategoryOverview />} />
                {/* Legacy /portal/health → /portal/categories */}
                <Route path="health" element={<Navigate to="/portal/categories" replace />} />
              </Route>

              {/* Intelligence Suite Style Detail route - outside nested layout for full page view */}
              <Route path="/portal/style/:id" element={<PortalRoute><StyleDetailView /></PortalRoute>} />

              {/* Reconcile routes — BRAND_ADMIN + FINANCE only */}
              <Route path="/reconcile" element={<FinanceRoute><ReconcileLayout /></FinanceRoute>}>
                <Route index element={<Navigate to="invoices" replace />} />
                <Route path="invoices" element={<ReconcileSelfInvoice />} />
                <Route path="weekly" element={<ReconcileWeekly />} />
              </Route>

              {/* Reports routes with nested structure */}
              <Route path="/reports" element={<ReportsLayout />}>
                {/* v2 (slice 2R): the library is /reports itself (ReportsLayout); these are v2 preview pages. */}
                <Route path="briefs" element={<V2PreviewRoute><BriefsPage /></V2PreviewRoute>} />
                <Route path="listings" element={<V2PreviewRoute><ListingStatusPage /></V2PreviewRoute>} />
                <Route path="discounts" element={<V2PreviewRoute><DiscountsPage /></V2PreviewRoute>} />
                <Route path="shipments" element={<V2PreviewRoute><ShipmentsSentPage /></V2PreviewRoute>} />
                <Route path="mine/:id" element={<V2PreviewRoute><SavedReportPage /></V2PreviewRoute>} />
                <Route path="sales" element={<SalesReports />}>
                  <Route index element={<SalesTransactions />} />
                  <Route path="transactions" element={<SalesTransactions />} />
                  <Route path="units" element={<SalesUnits />} />
                  <Route path="product-performance" element={<ProductPerformance />} />
                </Route>
                <Route path="inventory" element={<InventoryViewsLayout />}>
                  <Route index element={<InventoryReport />} />
                  <Route path="restock" element={<RestockRecommendations />} />
                  <Route path="snapshots" element={<InventorySnapshot />} />
                </Route>
                <Route path="returns" element={<ReturnsReports />}>
                  <Route index element={<ReturnsTransactions />} />
                  <Route path="transactions" element={<ReturnsTransactions />} />
                </Route>
              </Route>

              {/* P&L Statement — BRAND_ADMIN + FINANCE only.
                  Settled basis: ties out to the Reconciliation "Total to Pay".
                  (/admin/profit-loss below keeps the default shipped/accrual basis.) */}
              <Route path="/profit-loss" element={<FinanceRoute><PLStatement basis="settled" /></FinanceRoute>} />

              {/* Profile routes with nested structure */}
              <Route path="/profile" element={<ProfileLayout />}>
                <Route index element={<ProfileGeneral />} />
                <Route path="contact" element={<ProfileContact />} />
                <Route path="documents" element={<ProfileDocuments />} />
                <Route path="logo" element={<ProfileLogo />} />
                <Route path="marketplaces" element={<ProfileMarketplaces />} />
                <Route path="stores" element={<Navigate to="/profile/marketplaces" replace />} />
              </Route>

              {/* V2 preview surface — brands must not reach it in prod, even by typed URL
                  (master plan Amendment 2026-07-09). */}
              <Route path="/notifications" element={<V2PreviewRoute><Notifications /></V2PreviewRoute>} />

              {/* Onboarding route */}
              <Route path="/onboarding/sign/fake" element={<FakeSignPage />} />
              <Route path="/onboarding/*" element={<OnboardingPage />} />

              {/* Legacy marketplace URL (bookmarks only; nothing links here).
                  Redirects into the catalog's Marketplaces page. The catalog
                  guard still applies there, so in production, where the brand
                  catalog is off, a brand admin ends up on /reports. Accepted
                  by Ari 2026-09-23 until the brand catalog ships. */}
              <Route
                path="/marketplace"
                element={
                  <BrandAdminRoute>
                    <Navigate to={`${CATALOG_BASE}/marketplaces`} replace />
                  </BrandAdminRoute>
                }
              />

              {/* Catalog (AI Onboarding) — live for GB admins only (Ari,
                  2026-09-10); flag-gated. Admins are redirected by the guards
                  to the admin-shell twin below; this brand-shell mount is kept
                  for the brand rollout. One definition: catalogRoutes.tsx. */}
              {isCatalogEnabled() && catalogRoutes(CATALOG_BASE)}

              {/* Team Management (Brand Admin only) */}
              <Route path="/team" element={<BrandAdminRoute><TeamPage /></BrandAdminRoute>} />

              {/* Data Exports (Brand Admin + Admin/SUPER_ADMIN) */}
              <Route path="/company/exports" element={<ExportsRoute><DataExportsPage /></ExportsRoute>} />

              {/* Brand Hub — hubEnabled check is done client-side in Sidebar/page guards */}
              <Route path="/hub/pages/:pageId" element={<HubPageView />} />

              {/* My Reports — outside /hub/* so the sidebar shows the main nav, hubEnabled-gated */}
              <Route path="/my-reports" element={<MyReportsPage />} />
            </Route>

            {/* Admin routes - Separate layout with AdminSidebar */}
            <Route element={<AdminProtectedRoute />}>
              <Route path="/admin">
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                {isStudioEnabled() && <Route path="studio" element={<StudioPage />} />}
                {/* Operations page — warehouses (new) + nordstrom ops (relocated) */}
                <Route path="operations" element={<AdminOperations />} />
                {/* Registered ABOVE its `:brandId` sibling: the two share a position and React Router ranks STATIC above dynamic, so the brand-id parser never receives "feed" (see RejectionFeed.tsx). */}
                <Route path="operations/import-health/:instance/feed" element={<RejectionFeed />} />
                {/* `:instance` is mandatory — a brand exists on both
                    marketplaces with entirely different data. */}
                <Route
                  path="operations/import-health/:instance/:brandId"
                  element={<ImportHealthBrandDetail />}
                />
                <Route path="brands" element={<AdminBrandsList />} />
                <Route path="brands/:brandId" element={<AdminBrandDetail />} />
                {/* New admin management pages - to be implemented */}
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="users/:userId" element={<AdminUserDetail />} />
                <Route path="activity" element={<SuperAdminRoute><AdminUserActivityPage /></SuperAdminRoute>} />
                <Route path="promotions" element={<AdminPromotionsPage />} />
                <Route path="onboarding" element={<AdminOnboardingPage />} />
                <Route path="onboarding/brand/:brandId" element={<AdminOnboardingRecord />} />
                <Route path="onboarding/:submissionId" element={<OnboardingSubmissionDetail />} />
                {/* Action requests queue — GB-team side of Home's "Request a promotion" flow */}
                <Route path="action-requests" element={<AdminActionRequestsPage />} />
                {/* Catalog rejection verifier (Phase 7, Track E) — any admin */}
                <Route path="catalog-rejections" element={<AdminRejectionVerifier />} />
                {/* Catalog (AI Onboarding) inside the admin shell — the same
                    screens as /catalog/*, mounted here so clicking Catalog from
                    the admin sidebar never swaps the shell (Ari, 2026-09-10,
                    option 1). Brand is picked in CatalogBrandBar. */}
                {isCatalogEnabled() && catalogRoutes(ADMIN_CATALOG_BASE)}
                {/* Admin Portal routes - with brand context */}
                <Route path="portal" element={<AdminPortalLayout />}>
                  <Route index element={<PortalOverview />} />
                  <Route path="total" element={<PortalTotalView />} />
                  <Route path="categories" element={<PortalCategoryOverview />} />
                  {/* Legacy /admin/portal/health → /admin/portal/categories */}
                  <Route path="health" element={<Navigate to="/admin/portal/categories" replace />} />
                </Route>
                {/* Admin Portal Style Detail - outside nested layout for full page view, inherits brand context from AdminProtectedRoute */}
                <Route path="portal/style/:id" element={<StyleDetailView />} />
                {/* Admin Reports routes - with brand context (same pattern as admin portal) */}
                <Route path="reports" element={<AdminReportsLayout />}>
                  <Route path="sales" element={<SalesReports />}>
                    <Route index element={<SalesTransactions />} />
                    <Route path="transactions" element={<SalesTransactions />} />
                    <Route path="units" element={<SalesUnits />} />
                    <Route path="product-performance" element={<ProductPerformance />} />
                  </Route>
                  <Route path="inventory" element={<InventoryViewsLayout />}>
                    <Route index element={<InventoryReport />} />
                    <Route path="restock" element={<RestockRecommendations />} />
                    <Route path="snapshots" element={<InventorySnapshot />} />
                  </Route>
                  <Route path="returns" element={<ReturnsReports />}>
                    <Route index element={<ReturnsTransactions />} />
                    <Route path="transactions" element={<ReturnsTransactions />} />
                  </Route>
                </Route>
                {/* Admin P&L Statement - standalone with brand context */}
                <Route path="profit-loss" element={<PLStatement />} />
                {/* Admin Reconcile routes - with brand context */}
                <Route path="reconcile" element={<AdminReconcileLayout />}>
                  <Route index element={<Navigate to="invoices" replace />} />
                  <Route path="invoices" element={<ReconcileSelfInvoice />} />
                  <Route path="weekly" element={<ReconcileWeekly />} />
                </Route>
                {/* System Health route */}
                <Route path="system" element={<SuperAdminRoute><AdminSystemHealth /></SuperAdminRoute>} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
